(function () {
  'use strict';
  const S = window.WallForgeStudioState;
  const uid = () => crypto.randomUUID();
  const token = (capabilities, name) => capabilities?.[name]?.token || '';
  const visible = (node, show) => { if (node) node.hidden = !show; };
  const safeImage = value => {
    if (typeof value !== 'string' || !value.trim()) return '';
    try { const url = new URL(value, location.origin); return ['https:', 'http:'].includes(url.protocol) ? url.href : ''; }
    catch (_) { return ''; }
  };
  function containDialogFocus(dialog) {
    dialog.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const controls = Array.from(dialog.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),summary,[tabindex="0"]')).filter(node => node.checkVisibility ? node.checkVisibility() : node.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
  }
  const H = window.WallForgePortraitHistory;
  const storage = H.storage;

  class Studio {
    constructor(root) {
      this.root = root;
      this.product = JSON.parse(root.querySelector('[data-studio-product]').textContent);
      this.copy = JSON.parse(root.querySelector('[data-studio-copy]').textContent);
      this.key = [window.WallForgeAPI.baseUrl, window.Shopify?.shop || location.host, this.product.id].join('/');
      this.state = S.empty(this.product.id);
      this.catalog = S.catalog(this.product, window.WallforgeVariantSemantics);
      this.originalImage = root.querySelector('.wf-studio__image').cloneNode(true);
      let previewContract;
      try { previewContract = JSON.parse(this.el('[data-wallforge-live-preview-config]')?.textContent || '{}'); } catch (_) { previewContract = null; }
      this.productPreview = window.WallForgeProductPreview.create(this.el('[data-enlarge]'), previewContract, ratio => {
        this.el('[data-art-stage]').style.setProperty('--wf-preview-ratio', ratio);
        this.scheduleLayout?.();
      }, this.t('load_failed'));
      this.photoUrls = [];
      this.busy = false;
      this.ready = false;
      this.historyStatus = 'loading';
      const galleryLink = this.el('.wf-studio__all-portraits');
      const previewTheme = new URL(location.href).searchParams.get('preview_theme_id');
      if (galleryLink && previewTheme) {
        const galleryUrl = new URL(galleryLink.href); galleryUrl.searchParams.set('preview_theme_id', previewTheme); galleryLink.href = galleryUrl.href;
      }
      this.desktopLayout = matchMedia('(min-width: 960px)');
      this.examples = this.el('[data-support] .wf-studio__examples');
      this.onDesktopLayout = () => {
        if (!this.examples) return;
        if (this.desktopLayout.matches) this.el('[data-upload-panel]').append(this.examples);
        else this.el('[data-support]').prepend(this.examples);
      };
      this.desktopLayout.addEventListener('change', this.onDesktopLayout);
      this.onDesktopLayout();
      this.onPortraitHistory = () => this.updatePortraitCount();
      document.addEventListener('wallforge:history-updated', this.onPortraitHistory);
      this.previewRefreshes = new Map();
      this.previewErrorAttempts = new Map();
      this.onPreviewVisibility = () => { if (!document.hidden) this.refreshHistory(); };
      document.addEventListener('visibilitychange', this.onPreviewVisibility);
      this.previewRefreshTimer = setInterval(() => this.onPreviewVisibility(), 60000);
      this.onSizeUnit = () => { if (this.ready && !this.disposed) this.renderConfiguration(); };
      window.addEventListener('wallforge:size-unit', this.onSizeUnit);
      this.bind();
      this.viewportMeasure = document.createElement('span');
      this.viewportMeasure.className = 'wf-studio__viewport-measure';
      this.viewportMeasure.setAttribute('aria-hidden', 'true');
      this.root.append(this.viewportMeasure);
      this.scheduleLayout = () => {
        if (this.disposed || this.layoutFrame) return;
        this.layoutFrame = requestAnimationFrame(() => { this.layoutFrame = 0; this.measureLayout(); });
      };
      this.layoutObserver = new ResizeObserver(this.scheduleLayout);
      const header = document.querySelector('.wf-navigation');
      [header, this.root, this.el('.wf-studio__art-column'), this.el('[data-art-stage]'),
        this.el('.wf-studio__title-overlay h1'), this.el('[data-config-panel] legend'),
        this.el('.wf-studio__versions-heading'), this.el('[data-result-tools]'),
        ...this.root.querySelectorAll('.wf-studio__action,[data-result-tools] span')
      ].filter(Boolean).forEach(node => this.layoutObserver.observe(node));
      window.addEventListener('resize', this.scheduleLayout);
      window.visualViewport?.addEventListener('resize', this.scheduleLayout);
      this.scheduleLayout();
      this.root.addEventListener('focusin', event => {
        const result = this.root.dataset.screen === 'result';
        const configuring = this.root.dataset.screen === 'configure';
        if ((!result && !configuring) || !event.target.closest('.wf-studio__controls')) return;
        // Native Tab scrolling does not account for the sticky preview or bag bar.
        requestAnimationFrame(() => {
          if (document.activeElement === event.target) event.target.scrollIntoView({ block: 'nearest' });
        });
      });
      this.init();
    }
    el(selector) { return this.root.querySelector(selector); }
    measureConfigurationLayout() { this.scheduleLayout(); }
    layoutHeight() {
      // Safari's collapsing toolbars resize visualViewport during a scroll.
      // Small viewport units stay stable, but still respond to orientation/resize.
      return window.innerWidth < 960 ? this.viewportMeasure.getBoundingClientRect().height || window.innerHeight
        : window.visualViewport?.height || window.innerHeight;
    }
    measureLayout() {
      if (this.disposed) return;
      const pixels = value => parseFloat(value) || 0;
      const write = (name, value) => {
        if (this.root.style.getPropertyValue(name) !== value) this.root.style.setProperty(name, value);
      };
      const height = this.layoutHeight();
      const desktop = window.innerWidth >= 960;
      const headerNode = document.querySelector('.wf-navigation');
      const header = headerNode?.getBoundingClientRect().height || 0;
      const action = this.el(this.state.screen === 'configure' ? '[data-config-action]' : '[data-result-action]');
      const actionHeight = action.getBoundingClientRect().height;
      write('--wf-header-height', header + 'px');
      write('--wf-action-height', actionHeight + 'px');
      const overlay = this.el('.wf-studio__title-overlay');
      const overflowing = overlay.scrollHeight > overlay.clientHeight + 1;
      overlay.classList.toggle('wf-studio__title-scroll', overflowing);
      if (overflowing) overlay.setAttribute('tabindex', '0'); else overlay.removeAttribute('tabindex');
      const short = height <= (desktop ? 650 : 500);
      this.root.toggleAttribute('data-config-short-viewport', short);
      if (this.root.dataset.screen === 'configure') {
        const stage = this.el('.wf-studio__art-column'), style = getComputedStyle(stage);
        const paddingY = pixels(style.paddingTop) + pixels(style.paddingBottom);
        const width = stage.clientWidth - pixels(style.paddingLeft) - pixels(style.paddingRight);
        const ratio = Number(this.el('.wf-product-preview')?.dataset.previewRatio) || 2 / 3;
        const legend = this.el('[data-config-panel] legend');
        const controls = getComputedStyle(this.el('.wf-studio__controls'));
        const formatSpace = pixels(controls.paddingTop) + legend.getBoundingClientRect().height + 12;
        const available = height - header - actionHeight - formatSpace;
        const stageHeight = desktop ? Math.min(600, Math.max(300, height - header - 96))
          : Math.max(160, Math.min(600, width / ratio + paddingY, available));
        write('--wf-preview-height', Math.floor(stageHeight) + 'px');
      }
      this.measureResultLayout();
    }
    destroy() {
      this.disposed = true;
      this.desktopLayout.removeEventListener('change', this.onDesktopLayout);
      document.removeEventListener('wallforge:history-updated', this.onPortraitHistory);
      clearInterval(this.previewRefreshTimer);
      document.removeEventListener('visibilitychange', this.onPreviewVisibility);
      window.removeEventListener('wallforge:size-unit', this.onSizeUnit);
      this.layoutObserver.disconnect();
      if (this.layoutFrame) cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = 0;
      window.removeEventListener('resize', this.scheduleLayout);
      window.visualViewport?.removeEventListener('resize', this.scheduleLayout);
      this.productPreview.destroy();
      this.viewportMeasure.remove();
    }
    measureResultLayout() {
      if (this.root.dataset.screen !== 'result') return;
      const desktop = window.innerWidth >= 960;
      const height = this.layoutHeight();
      const header = document.querySelector('.wf-navigation')?.getBoundingClientRect().height || 0;
      const action = desktop ? 0 : this.el('[data-result-action]').getBoundingClientRect().height;
      // Reserve the heading, not the entire thumbnail strip: artwork comes first.
      const versions = desktop ? 0 : this.el('.wf-studio__versions-heading').getBoundingClientRect().height + 10;
      const available = height - header - action;
      const limit = Math.max(288, available - versions - (desktop ? 64 : 16));
      const tools = this.el('[data-result-tools]'), row = getComputedStyle(tools);
      const buttonWidth = Math.max(...Array.from(tools.children, button => {
        const style = getComputedStyle(button);
        return button.querySelector('span').getBoundingClientRect().width + 17 + parseFloat(style.gap) + parseFloat(style.paddingLeft) + parseFloat(style.paddingRight) + 2;
      }));
      const minimumWidth = Math.ceil(buttonWidth * 2 + parseFloat(row.gap) + parseFloat(row.left) + parseFloat(row.right));
      this.root.style.setProperty('--wf-result-art-limit', limit + 'px');
      this.root.style.setProperty('--wf-result-min-width', minimumWidth + 'px');
      // At large text sizes give both labels enough width and allow the portrait
      // to scroll, instead of breaking "Retry" or shrinking the customer's text.
      this.root.toggleAttribute('data-short-result', available < 320 || minimumWidth * 1.5 > limit);
    }
    t(key) { return this.copy[key] || key; }
    error(message) {
      const node = this.el('[data-error]'); node.textContent = message || ''; visible(node, Boolean(message));
      if (message && this.ready && !document.querySelector('dialog[open]')) requestAnimationFrame(() => {
        if (node.hidden || node.textContent !== message) return;
        const target = this.state.flowScreen ? this.el('[data-generation-title]') : node;
        target.tabIndex = -1; target.focus({ preventScroll: true }); target.scrollIntoView({ block: 'center' });
      });
    }
    async persist() {
      if (this.historyStatus !== 'ready') throw new Error('History has not loaded');
      this.state.design = { title: this.product.title, handle: this.product.handle };
      const snapshot = window.WallForgePortraitHistory.snapshot(this.state);
      // Photo progress can arrive while a generation finishes. Preserve write order
      // across asynchronous File encoding so an older snapshot cannot erase it.
      this.persistQueue = (this.persistQueue || Promise.resolve()).catch(() => {}).then(() => storage(this.key, snapshot));
      await this.persistQueue;
    }
    event(name, details) {
      const detail = Object.assign({ product_id: String(this.product.id), design_id: String(this.product.id) }, details);
      document.dispatchEvent(new CustomEvent('wallforge:' + name, { detail }));
      window.dataLayer?.push(Object.assign({ event: 'wallforge_' + name }, detail));
    }
    async init() {
      if (this.initializing) return;
      this.initializing = true;
      this.error(''); visible(this.el('[data-recover]'), false);
      try {
        if (this.historyStatus !== 'ready') {
          this.historyStatus = 'loading'; this.renderHistoryStatus();
          const saved = await storage(this.key);
          if (this.disposed) return;
          if (saved?.contract === 1 && saved.designId === String(this.product.id)) {
            this.state = saved;
            const entry = new URL(location.href);
            if (!['wf_portrait', 'wf_revision', 'wf_saved'].some(key => entry.searchParams.has(key)) && !entry.hash.startsWith('#wf-portrait=')) S.enterProduct(this.state);
          }
          this.historyStatus = 'ready';
        }
        if (this.state.attempt && this.state.flowScreen === undefined) this.state.flowScreen = 'generating';
        const requestedSave = new URL(location.href).searchParams.get('wf_saved');
        const record = this.state.savedPortraits?.find(item => item.id === requestedSave);
        if (record && this.state.versions.some(item => item.id === record.revisionId)) {
          this.state.selectedId = record.revisionId; this.state.variantId = record.variantId; this.state.configuration = { id: record.variantId }; this.state.screen = 'result';
          const url = new URL(location.href); url.searchParams.delete('wf_saved'); history.replaceState(history.state, '', url.href);
          await this.persist();
        }
        const cartUrl = new URL(location.href), cartRevision = cartUrl.searchParams.get('wf_revision');
        if (cartRevision) {
          if (this.state.versions.some(item => item.id === cartRevision)) {
            this.state.selectedId = cartRevision; this.state.screen = 'configure';
            const variantId = cartUrl.searchParams.get('variant');
            const choice = this.variants().find(item => item.id === variantId && item.available);
            this.state.variantId = choice?.id || null;
            this.state.configuration = variantId ? { id: variantId, type: choice?.type, frame: choice?.frame, size: choice?.size } : null;
          } else { this.cartRestoreUnavailable = true; this.state.screen = 'upload'; }
          cartUrl.searchParams.delete('wf_revision'); history.replaceState(history.state, '', cartUrl.href); await this.persist();
        }
        if (!S.selected(this.state) && !this.state.attempt) { this.state.selectedId = null; this.state.screen = 'upload'; }
        const portraitUrl = new URL(location.href), portraitId = portraitUrl.searchParams.get('wf_portrait');
        if (portraitId) {
          if (S.selectVersion(this.state, portraitId)) { this.state.screen = 'result'; this.state.flowScreen = null; }
          else { this.state.selectedId = null; this.state.screen = 'upload'; }
          portraitUrl.searchParams.delete('wf_portrait'); history.replaceState(history.state, '', portraitUrl.href);
          await this.persist();
        }
        if (this.state.screen !== 'upload') S.restoreSources(this.state);
        this.render();
        await this.discover();
        if (this.disposed) return;
        this.ready = true;
        this.updatePortraitCount();
        if (location.hash.startsWith('#wf-portrait=')) {
          this.resumeIdentity = location.hash.slice('#wf-portrait='.length);
          history.replaceState(history.state, '', location.pathname + location.search);
          await this.resume();
        }
        this.render();
        if (portraitId && S.selected(this.state)) await this.refreshSelected();
        else if (this.state.attempt && this.state.flowScreen && ['poll', 'edit_poll'].includes(this.state.attempt.phase)) this.poll();
        else if (this.state.attempt && this.state.flowScreen) { this.state.flowScreen = 'generation-delayed'; this.error(this.t('interrupted')); visible(this.el('[data-recover]'), true); }
        // Preview renewal is optional background work, not uploader bootstrap.
        else this.refreshHistory(true).catch(() => {});
      } catch (_) {
        if (this.historyStatus === 'loading') this.historyStatus = 'error';
        else { this.error(this.t('load_failed')); visible(this.el('[data-recover]'), true); }
      }
      finally {
        this.initializing = false;
        if (this.disposed) return;
        this.render();
        if (this.cartRestoreUnavailable) this.error(this.t('cart_restore_unavailable'));
        history.replaceState({ ...history.state, wallforgeDesign: this.state.designId, screen: this.state.screen }, '', location.href);
        if (location.hash === '#wf-upload') this.el('[data-upload-target]').scrollIntoView({ block: 'center' });
      }
    }
    async discover(refresh = false) {
      this.discovery = await window.WallForgeUploadPolicy.discover(String(this.product.id), { refresh });
      this.policy = this.discovery.uploadPolicy;
      const hint = this.el('[data-upload-hint]');
      if (hint) {
        hint.dataset.defaultHint ||= hint.textContent;
        const capacity = this.policy.subjectCapacity;
        hint.textContent = capacity ? this.t(capacity.max === 1 ? 'capacity_one' : capacity.max === 2 ? 'capacity_two' : 'capacity_many') : hint.dataset.defaultHint;
      }
      this.photoPreparation = this.policy.photoPreparation || null;
      this.creationAvailable = true; this.personalizationPayload = null;
      if (this.discovery.enabled) {
        const values = {};
        (this.discovery.fields || []).forEach(field => {
          const value = field.defaultOptionId ?? field.defaultValue ?? null;
          if (field.required && (value === null || String(value).trim() === '')) this.creationAvailable = false;
          values[field.key] = value;
        });
        this.personalizationPayload = { schemaId: this.discovery.schemaId, schemaVersion: this.discovery.version, schemaHash: this.discovery.hash, values };
        if (!this.creationAvailable) this.error(this.t('design_unavailable'));
      }
      this.root.querySelectorAll('[data-photo-input],[data-retry-photo-input]').forEach(input => {
        input.accept = this.policy.allowedMimeTypes.join(',');
        input.multiple = this.policy.selectionFileLimit > 1;
        input.disabled = false;
      });
      this.el('[data-upload-formats]').textContent = this.policy.allowedMimeTypes.map(type => type.replace('image/', '').toUpperCase()).filter((type, i, all) => all.indexOf(type) === i).join(' · ');
      if (this.photoPreparation) {
        this.state.photos.forEach(photo => this.preparePhoto(photo).catch(() => {}));
      }
    }
    bind() {
      const click = (selector, action) => this.el(selector)?.addEventListener('click', action);
      this.el('[data-photo-input]').addEventListener('change', event => { const files = Array.from(event.target.files || []); event.target.value = ''; if (files.length) this.addPhotos(files); });
      const target = this.el('[data-upload-target]');
      target.addEventListener('dragover', event => event.preventDefault());
      target.addEventListener('drop', event => { event.preventDefault(); this.addPhotos(Array.from(event.dataTransfer.files || [])); });
      click('[data-generate]', () => this.generate());
      click('[data-retry]', () => this.openRetry());
      click('[data-retry-same]', () => {
        const source = this.retrySource;
        if (!S.photosFor(source).length || this.submitting || this.busy) return;
        this.el('[data-retry-dialog]').close();
        this.generate(source);
      });
      click('[data-retry-new]', () => this.el('[data-retry-photo-input]').click());
      this.el('[data-retry-photo-input]').addEventListener('change', event => {
        const files = Array.from(event.target.files || []); event.target.value = '';
        if (files.length) this.replacePhotos(files, this.retrySource);
      });
      click('[data-recover]', () => this.recover());
      click('[data-history-retry]', () => this.init());
      click('[data-generation-wait]', () => { this.showGeneration(); if (!this.busy) this.recover(); });
      click('[data-generation-photos]', () => { const issue = this.state.generationIssue; this.screen('upload'); this.error(issue?.changePhotos ? this.t(issue.key) : ''); });
      click('[data-generation-result]', () => { this.screen('result'); this.error(''); });
      click('[data-view-generation]', () => { this.showGeneration(); if (!this.busy) this.recover(); });
      click('[data-generation-retry]', () => {
        const failed = this.state.failedAttempt;
        if (failed?.kind === 'edit') {
          this.editSource = this.state.versions.find(version => version.id === failed.sourceId);
          if (this.editSource) this.edit(failed.prompt);
        } else this.generate();
      });
      click('[data-configure]', () => this.screen('configure'));
      click('[data-all-versions]', () => window.WallForgePortraitLibrary.openPicker(this));
      click('[data-add-bag]', () => this.addBag());
      this.root.querySelectorAll('[data-size-unit]').forEach(input => input.addEventListener('change', () => {
        if (input.checked) window.WallForgeSizeDisplay.setUnit(input.value);
      }));
      click('[data-edit]', () => { this.editSource = structuredClone(S.selected(this.state)); this.open(this.el('[data-edit-dialog]')); });
      click('[data-saved-continue]', () => { this.el('[data-save-dialog]').close(); this.screen('configure'); });
      this.root.querySelectorAll('[data-scroll-upload]').forEach(button => button.addEventListener('click', () => {
        this.el('[data-upload-target]').scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        this.el('[data-photo-input]').focus({ preventScroll: true });
      }));
      click('[data-enlarge]', () => this.lightbox(this.el('.wf-studio__image').currentSrc || this.el('.wf-studio__image').src));
      click('[data-lightbox-zoom]', () => {
        const button = this.el('[data-lightbox-zoom]'), zoomed = button.getAttribute('aria-pressed') !== 'true';
        button.setAttribute('aria-pressed', String(zoomed)); button.textContent = zoomed ? button.dataset.fitLabel : button.dataset.zoomLabel;
        this.el('[data-lightbox]').classList.toggle('is-zoomed', zoomed);
      });
      this.root.querySelectorAll('[data-example]').forEach(button => button.addEventListener('click', () => this.lightbox(button.querySelector('img').src)));
      this.root.querySelectorAll('[data-close]').forEach(button => button.addEventListener('click', () => button.closest('dialog').close()));
      this.root.querySelectorAll('dialog').forEach(dialog => {
        containDialogFocus(dialog);
        dialog.addEventListener('click', event => { if (event.target === dialog) { const r = dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close(); } });
        dialog.addEventListener('close', () => { if (!document.querySelector('dialog[open]')) dialog.returnFocus?.focus(); });
      });
      this.el('[data-edit-form]').addEventListener('submit', event => {
        event.preventDefault(); const input = event.currentTarget.elements.prompt, prompt = input.value.trim();
        input.setCustomValidity(prompt ? '' : this.t('describe_edit'));
        if (prompt) this.edit(prompt); else input.reportValidity();
      });
      this.el('[data-edit-form] [name=prompt]').addEventListener('input', event => {
        event.target.setCustomValidity(''); this.el('[data-edit-form] [type=submit]').disabled = !event.target.value.trim();
      });
      this.el('[data-save-form]').addEventListener('submit', event => { event.preventDefault(); this.savePortrait(event.currentTarget); });
      this.el('[data-save-form] [name=email]').addEventListener('input', event => {
        event.target.removeAttribute('aria-invalid');
        if (this.saveValidationError) { this.el('[data-save-status]').textContent = ''; this.saveValidationError = false; }
      });
      window.addEventListener('popstate', event => {
        if (event.state?.wallforgeDesign !== this.state.designId || !['upload', 'result', 'configure'].includes(event.state.screen)) return;
        if (event.state.screen !== 'upload' && !S.selected(this.state)) { this.returnToUpload(); return; }
        this.state.screen = event.state.screen; if (this.state.screen !== 'upload') S.restoreSources(this.state); this.state.flowScreen = null; this.render(); this.persist().catch(() => {});
        this.root.tabIndex = -1; this.root.focus({ preventScroll: true });
        this.refreshHistory();
      });
      window.addEventListener('pageshow', event => { if (event.persisted) { if (this.state.flowScreen && this.state.attempt?.phase.endsWith('poll')) this.poll(); else this.refreshHistory(); } });
      window.addEventListener('online', () => { if (this.state.flowScreen && this.state.attempt?.phase.endsWith('poll')) this.poll(); else this.refreshHistory(); });
      this.root.addEventListener('error', event => {
        if (!(event.target instanceof HTMLImageElement)) return;
        const id = event.target.closest('[data-version-id]')?.dataset.versionId;
        const version = id ? S.completedVersions(this.state).find(item => item.id === id)
          : event.target.closest('[data-art-stage],[data-lightbox]') ? S.selected(this.state) : null;
        if (!version || !this.ready) return;
        // A missing image or outage must never cause an endless request/render loop.
        const last = this.previewErrorAttempts.get(version.id) || 0;
        if (Date.now() - last < 60000) return;
        this.previewErrorAttempts.set(version.id, Date.now());
        this.refreshVersion(version, true);
      }, true);
      window.addEventListener('hashchange', async () => {
        if (!this.ready || !location.hash.startsWith('#wf-portrait=')) return;
        this.resumeIdentity = location.hash.slice('#wf-portrait='.length);
        history.replaceState(history.state, '', location.pathname + location.search);
        await this.resume(); this.render();
      });
    }
    open(dialog) {
      if (dialog.matches('[data-save-dialog]')) {
        this.saveViewId = uid(); dialog.querySelector('[type=submit]').disabled = false; dialog.querySelector('[name=email]').disabled = false;
      }
      if (dialog.matches('[data-edit-dialog]')) dialog.querySelector('[type=submit]').disabled = !dialog.querySelector('[name=prompt]').value.trim();
      dialog.returnFocus = document.activeElement; dialog.showModal(); dialog.querySelector('textarea,input')?.focus();
    }
    lightbox(url) {
      const image = safeImage(url); if (!image) return;
      const dialog = this.el('[data-lightbox]'), zoom = this.el('[data-lightbox-zoom]');
      dialog.classList.remove('is-zoomed'); zoom.setAttribute('aria-pressed', 'false'); zoom.textContent = zoom.dataset.zoomLabel;
      dialog.querySelector('img').src = image; this.open(dialog);
    }
    async screen(screen) {
      if (screen !== 'upload' && !S.selected(this.state)) return;
      this.state.flowScreen = null;
      this.state.screen = screen;
      if (screen !== 'upload') S.restoreSources(this.state);
      if (history.state?.wallforgeDesign !== this.state.designId || history.state.screen !== screen) history.pushState({ ...history.state, wallforgeDesign: this.state.designId, screen }, '', location.href);
      this.render();
      this.root.tabIndex = -1; this.root.focus({ preventScroll: true });
      await this.persist();
      await new Promise(resolve => requestAnimationFrame(resolve));
      this.root.scrollIntoView({ block: 'start' });
      if (screen === 'configure') this.event('configuration', { revision_id: this.state.selectedId, variant_id: this.state.variantId });
    }
    variants() { return S.allowed(this.catalog, S.selected(this.state)); }
    variant() { return this.variants().find(variant => variant.id === this.state.variantId); }
    money(price) {
      return window.theme?.Currency?.formatMoney ? window.theme.Currency.formatMoney(price, window.theme.settings?.moneyFormat)
        : new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: window.Shopify?.currency?.active || this.root.dataset.currency }).format(price / 100);
    }
    render() {
      const state = this.state, version = S.selected(state), processing = Boolean(state.attempt && this.busy);
      if (!version && state.screen !== 'upload') state.screen = 'upload';
      this.root.dataset.screen = state.flowScreen || state.screen;
      visible(this.el('[data-product-hero]'), !state.flowScreen);
      visible(this.el('[data-upload-panel]'), state.screen === 'upload');
      visible(this.el('[data-result-panel]'), state.screen === 'result');
      visible(this.el('[data-result-tools]'), state.screen === 'result' && !state.flowScreen);
      visible(this.el('[data-config-panel]'), state.screen === 'configure');
      visible(this.el('[data-support]'), state.screen === 'upload' && !state.flowScreen);
      visible(this.el('[data-job-panel]'), Boolean(state.flowScreen));
      visible(this.el('[data-view-generation]'), Boolean(state.attempt) && !state.flowScreen);
      visible(this.el('[data-studio-stepper]'), Boolean(state.flowScreen));
      const step = state.flowScreen ? (state.attempt?.kind === 'edit' || state.failedAttempt?.kind === 'edit' ? 1 : 0) : state.screen === 'configure' ? 2 : state.screen === 'result' ? 1 : 0;
      this.root.querySelectorAll('[data-journey-step]').forEach(node => {
        const index = Number(node.dataset.journeyStep);
        node.classList.toggle('is-complete', index < step); node.classList.toggle('is-current', index === step);
        if (index === step) node.setAttribute('aria-current', 'step'); else node.removeAttribute('aria-current');
      });
      this.renderGeneration();
      const generate = this.el('[data-generate]');
      generate.disabled = !this.ready || !this.creationAvailable || !state.photos.length || Boolean(state.attempt) || this.busy;
      generate.dataset.processing = String(processing);
      this.el('[data-retry]').disabled = !this.ready || !this.creationAvailable || Boolean(state.attempt) || this.busy;
      this.el('[data-edit]').disabled = Boolean(state.attempt) || this.busy || !token(version?.capabilities, 'editCreate');
      const image = this.el('.wf-studio__image');
      if (state.screen !== 'upload' && version) {
        image.removeAttribute('srcset'); if (image.src !== safeImage(version.previewUrl)) image.src = safeImage(version.previewUrl); image.alt = this.product.title;
      } else {
        image.src = this.originalImage.src;
        if (this.originalImage.hasAttribute('srcset')) image.setAttribute('srcset', this.originalImage.getAttribute('srcset'));
      }
      const feedback = this.el('.wf-studio__feedback');
      const feedbackParent = state.flowScreen ? this.el('[data-job-panel]') : this.el('.wf-studio__controls');
      if (feedback.parentElement !== feedbackParent) feedbackParent.append(feedback);
      this.renderPhotos();
      this.renderVersions();
      this.renderConfiguration();
    }
    showGeneration() {
      const entering = !this.state.flowScreen;
      this.state.flowScreen = 'generating'; this.error(''); this.render();
      clearTimeout(this.generationDelay);
      this.generationDelay = setTimeout(() => {
        if (this.state.flowScreen === 'generating' && (this.state.attempt || this.busy)) {
          this.state.flowScreen = 'generation-delayed'; this.render(); this.persist().catch(() => {});
        }
      }, 60000);
      if (entering) requestAnimationFrame(() => {
        if (!this.state.flowScreen) return;
        this.root.scrollIntoView({ block: 'start' }); this.el('[data-generation-title]').focus({ preventScroll: true });
      });
    }
    renderGeneration() {
      const flow = this.state.flowScreen;
      if (!flow) return;
      const attempt = this.state.attempt, failed = flow === 'generation-error', delayed = flow === 'generation-delayed', issue = this.state.generationIssue;
      this.el('[data-generation-title]').textContent = this.t(failed ? 'generation_error_title' : delayed ? 'generation_delayed_title' : 'generation_title');
      this.el('[data-generation-copy]').textContent = this.t(failed ? 'generation_error_copy' : delayed ? 'generation_delayed_copy' : 'generation_copy');
      const source = attempt?.kind === 'edit' ? this.state.versions.find(version => version.id === attempt.sourceId) : null;
      const file = this.state.photos[0]?.file;
      if (file !== this.generationPhotoFile) {
        if (this.generationPhotoUrl) URL.revokeObjectURL(this.generationPhotoUrl);
        this.generationPhotoFile = file; this.generationPhotoUrl = file ? URL.createObjectURL(file) : null;
      }
      const preview = this.generationPhotoUrl || safeImage(source?.previewUrl || S.selected(this.state)?.previewUrl);
      const image = this.el('[data-generation-photo]');
      if (preview) image.src = preview;
      visible(image, Boolean(preview));
      this.el('.wf-studio__generation-photo').classList.toggle('is-empty', !preview);
      const stage = attempt?.processingStage || '';
      let current = ['evaluation_queued', 'evaluating_output', 'publishing_result', 'preview_ready'].includes(stage) || attempt?.phase === 'edit_accept' ? 2
        : ['generation_queued', 'generating'].includes(stage) || attempt?.kind === 'edit' ? 1 : 0;
      this.root.querySelectorAll('[data-generation-stage]').forEach(node => {
        const index = Number(node.dataset.generationStage);
        node.classList.toggle('is-complete', index < current); node.classList.toggle('is-current', index === current);
        if (index === current) node.setAttribute('aria-current', 'step'); else node.removeAttribute('aria-current');
      });
      visible(this.el('[data-generation-progress]'), !failed);
      visible(this.el('[data-generation-stages]'), !failed);
      visible(this.el('[data-generation-wait]'), delayed && this.busy);
      visible(this.el('[data-generation-retry]'), failed && !attempt && !issue?.allowance && !issue?.changePhotos && Boolean(this.state.photos.length || this.state.failedAttempt?.kind === 'edit'));
      visible(this.el('[data-generation-photos]'), (failed && !issue?.allowance) || delayed);
      visible(this.el('[data-generation-result]'), (failed || delayed) && Boolean(S.selected(this.state)));
      visible(this.el('[data-generation-browse]'), failed && Boolean(issue?.allowance || issue?.changePhotos));
      const resultAction = this.el('[data-generation-result]'), resultPrimary = failed && this.el('[data-generation-retry]').hidden;
      resultAction.classList.toggle('wf-studio__primary', resultPrimary); resultAction.classList.toggle('wf-studio__text-button', !resultPrimary);
      const browseAction = this.el('[data-generation-browse]'), browsePrimary = failed && issue?.allowance && !S.selected(this.state);
      browseAction.classList.toggle('wf-studio__primary', Boolean(browsePrimary)); browseAction.classList.toggle('wf-studio__text-button', !browsePrimary);
      if (failed && issue && !this.el('[data-error]').textContent) this.error(this.t(issue.key));
      const message = failed ? '' : delayed ? this.t('delayed') : attempt?.phase === 'transfer' ? this.t('generation_uploading') : attempt?.status === 'queued' ? this.t('queued') : '';
      if (this.el('[data-job-status]').textContent !== message) this.el('[data-job-status]').textContent = message;
    }
    renderPhotos() {
      this.photoUrls.forEach(url => URL.revokeObjectURL(url)); this.photoUrls = [];
      const list = this.el('[data-photos]'); list.replaceChildren();
      visible(this.el('[data-picker]'), !this.state.photos.length);
      this.el('[data-photo-input]').disabled = !this.policy || !this.creationAvailable || this.busy || Boolean(this.state.attempt);
      this.state.photos.forEach(photo => {
        const item = document.createElement('div'); item.className = 'wf-studio__photo';
        const image = document.createElement('img'); image.src = URL.createObjectURL(photo.file); image.alt = this.t('reference'); this.photoUrls.push(image.src);
        const remove = document.createElement('button'); remove.type = 'button'; remove.textContent = '×'; remove.setAttribute('aria-label', this.t('remove_photo')); remove.disabled = this.busy || Boolean(this.state.attempt);
        remove.addEventListener('click', async () => { if (this.photoPreparation) window.WallForgePhotoPreparation.cancel(photo.id, this.photoPreparation); this.state.photos = this.state.photos.filter(item => item.id !== photo.id); await this.persist(); this.render(); this.error(''); });
        if (this.photoPreparation && photo.preparationState === 'failed') {
          const status = document.createElement('span'); status.className = 'wf-studio__photo-status'; status.setAttribute('role', 'status');
          status.textContent = this.t('photo_failed');
          item.append(status);
          const retry = document.createElement('button'); retry.type = 'button'; retry.className = 'wf-studio__photo-retry'; retry.textContent = this.t('photo_retry');
          retry.disabled = this.busy || Boolean(this.state.attempt);
          retry.onclick = () => { photo.preparation = null; photo.preparationAttemptId = uid(); this.preparePhoto(photo).catch(() => {}); };
          item.append(retry);
        }
        item.append(image, remove); list.append(item);
      });
      if (this.state.photos.length && this.policy && this.state.photos.length < this.policy.selectionFileLimit) {
        const add = document.createElement('button'); add.type = 'button'; add.className = 'wf-studio__photo-add';
        const plus = document.createElement('span'); plus.textContent = '+'; plus.setAttribute('aria-hidden', 'true');
        const label = document.createElement('span'); label.textContent = this.t('add_photo'); add.append(plus, label);
        add.setAttribute('aria-label', this.t('add_photo')); add.disabled = this.busy || Boolean(this.state.attempt); add.onclick = () => this.el('[data-photo-input]').click(); list.append(add);
      }
    }
    renderVersions() {
      this.renderHistoryStatus();
      const focusedList = document.activeElement?.closest('[data-versions],[data-previous-list]');
      const focusedId = document.activeElement?.dataset.versionId;
      const versions = S.completedVersions(this.state);
      this.el('[data-version-count]').textContent = this.t('version_count').replace('{count}', String(Math.max(versions.length, this.portraitCount || 0)));
      this.el('[data-previous-count]').textContent = String(versions.length);
      visible(this.el('[data-previous-versions]'), versions.length > 0);
      const list = this.el('[data-versions]'), previous = this.el('[data-previous-list]');
      list.replaceChildren(); previous.replaceChildren();
      versions.forEach(version => {
        const number = S.versionNumber(this.state, version.id);
        const button = document.createElement('button'); button.type = 'button'; button.setAttribute('aria-label', this.t('version') + ' ' + number); button.setAttribute('aria-pressed', String(version.id === this.state.selectedId));
        button.dataset.versionId = version.id;
        const image = document.createElement('img'); image.src = safeImage(version.previewUrl); image.alt = ''; image.width = 72; image.height = 108;
        const label = document.createElement('span'); label.textContent = String(number);
        button.append(image, label); button.onclick = () => this.selectVersion(version.id);
        list.append(button);
        const item = document.createElement('li'), previousButton = document.createElement('button');
        previousButton.type = 'button'; previousButton.className = 'wf-studio__previous-version'; previousButton.dataset.versionId = version.id;
        previousButton.setAttribute('aria-label', this.t('open_version').replace('{number}', String(number)));
        if (version.id === this.state.selectedId) previousButton.setAttribute('aria-current', 'true');
        const frame = document.createElement('span'); frame.className = 'wf-studio__previous-image';
        const thumbnail = image.cloneNode(true); thumbnail.loading = 'lazy';
        frame.append(thumbnail);
        const caption = document.createElement('span'); caption.className = 'wf-studio__previous-label'; caption.textContent = this.t('version') + ' ' + number;
        previousButton.append(frame, caption); previousButton.onclick = () => this.selectVersion(version.id, true);
        item.append(previousButton); previous.prepend(item);
      });
      if (focusedList && !focusedList.closest('[hidden]')) Array.from(focusedList.querySelectorAll('[data-version-id]')).find(button => button.dataset.versionId === focusedId)?.focus({ preventScroll: true });
    }
    renderHistoryStatus() {
      const status = this.el('[data-history-status]');
      if (!status) return;
      this.root.dataset.historyState = this.historyStatus;
      visible(status, this.historyStatus !== 'ready');
      this.el('[data-history-message]').textContent = this.t(this.historyStatus === 'error' ? 'history_load_failed' : 'history_loading');
      visible(this.el('[data-history-retry]'), this.historyStatus === 'error');
    }
    async updatePortraitCount() {
      if (this.disposed || this.countPending) return;
      this.countPending = true;
      try {
        this.portraitCount = H.count(await H.groups());
        if (!this.disposed) this.el('[data-version-count]').textContent = this.t('version_count').replace('{count}', String(Math.max(S.completedVersions(this.state).length, this.portraitCount)));
      } catch (_) { /* The active design remains usable if history cannot load. */ }
      finally { this.countPending = false; }
    }
    async selectVersion(id, openResult = false) {
      if (!S.selectVersion(this.state, id)) return;
      if (!this.variants().some(item => item.id === this.state.variantId && item.available)) this.state.variantId = null;
      if (openResult) await this.screen('result');
      else { await this.persist(); this.render(); }
      this.event('version_selected', { revision_id: id });
      await this.refreshSelected();
    }
    optionButtons(selector, values, selected, change, label = value => value, available = () => true) {
      const list = this.el(selector); list.replaceChildren();
      values.forEach(value => { const button = document.createElement('button'); button.type = 'button'; button.dataset.value = value; button.textContent = label(value); button.disabled = this.busy || !available(value); button.setAttribute('aria-pressed', String(value === selected)); button.onclick = () => change(value); list.append(button); });
    }
    sizeLabel(size) {
      return window.WallForgeSizeDisplay.label(size, window.WallForgeSizeDisplay.getUnit(), { in: this.t('size_inches'), cm: this.t('size_centimeters') });
    }
    renderConfiguration() {
      this.root.querySelectorAll('[data-size-unit]').forEach(input => { input.checked = input.value === window.WallForgeSizeDisplay.getUnit(); });
      const focusedOptions = document.activeElement?.closest('.wf-studio__options');
      const variants = this.variants();
      if (this.state.screen === 'configure') S.initializeConfiguration(this.state, variants);
      if (!this.state.variantId && !this.state.configuration) this.state.variantId = S.choose(variants)?.id || null;
      const variant = this.variant();
      const activeType = variant?.type || this.state.configuration?.type || (variants.some(v => v.type === 'poster') ? 'poster' : variants[0]?.type);
      const activeFrame = variant?.frame || this.state.configuration?.frame;
      this.optionButtons('[data-formats]', ['poster', 'canvas', 'digital'].filter(type => variants.some(v => v.type === type)), activeType, type => this.choose({ type }), type => this.t(type), type => variants.some(v => v.type === type && v.available));
      this.optionButtons('[data-frames]', [...new Set(variants.filter(v => v.type === activeType).map(v => v.frame))], activeFrame, frame => this.choose({ type: activeType, frame, size: variant?.size }), frame => frame === 'none' ? this.t('no_frame') : frame.charAt(0).toUpperCase() + frame.slice(1));
      this.optionButtons('[data-sizes]', [...new Set(variants.filter(v => v.type === activeType && v.frame === activeFrame).sort((a,b) => a.area-b.area).map(v => v.size))], variant?.size, size => this.choose({ type: activeType, frame: activeFrame, size }), value => this.sizeLabel(value), size => variants.some(v => v.type === activeType && v.frame === activeFrame && v.size === size && v.available));
      visible(this.el('[data-frame-field]'), Boolean(activeType && activeType !== 'digital')); visible(this.el('[data-size-field]'), Boolean(activeType && activeType !== 'digital'));
      visible(this.el('[data-entitlement]'), activeType !== 'digital');
      this.el('[data-fit-message]').textContent = variants.length ? '' : this.t('fit_unavailable');
      visible(this.el('[data-digital-details]'), activeType === 'digital');
      this.el('[data-digital-price]').textContent = variant ? this.money(variant.price) : '';
      this.el('[data-formats]').querySelectorAll('button').forEach(button => {
        const src = this.el('[data-formats]').dataset[button.dataset.value + 'Icon'];
        if (src) { const icon = document.createElement('img'); icon.src = src; icon.alt = ''; icon.width = 52; icon.height = 52; button.prepend(icon); }
      });
      this.el('[data-frames]').querySelectorAll('button').forEach(button => {
        const sample = document.createElement('span'); sample.className = 'wf-studio__frame-swatch'; sample.dataset.frame = button.dataset.value; sample.setAttribute('aria-hidden', 'true'); button.prepend(sample);
        const priced = variants.find(v => v.type === activeType && v.frame === button.dataset.value && v.size === variant?.size);
        const base = variants.find(v => v.type === activeType && v.frame === 'none' && v.size === variant?.size);
        if (!priced?.available && variant) { button.disabled = true; const cost = document.createElement('small'); cost.textContent = this.t('unavailable'); button.append(cost); }
        else if (priced && base) { const cost = document.createElement('small'); cost.textContent = button.dataset.value === 'none' ? this.t('included') : (priced.price >= base.price ? '+' : '−') + this.money(Math.abs(priced.price - base.price)); button.append(cost); }
      });
      this.el('[data-sizes]').querySelectorAll('button').forEach(button => {
        const priced = variants.find(v => v.type === activeType && v.frame === activeFrame && v.size === button.dataset.value);
        const cost = document.createElement('strong'); cost.textContent = priced ? this.money(priced.price) : ''; button.append(cost);
      });
      const bag = this.el('[data-add-bag]'); bag.disabled = !variant?.available || !S.selected(this.state) || this.busy;
      this.el('[data-bag-label]').textContent = this.t('add_bag') + (variant ? ' · ' + this.money(variant.price) : '');
      this.el('[data-configuration-summary]').textContent = variant ? variant.isDigital ? this.t('digital') + ' · ' + this.t('digital_only') : [this.t(variant.type) + ' + ' + this.t('digital').toLowerCase(), variant.frame === 'none' ? this.t('no_frame') : variant.frame.charAt(0).toUpperCase() + variant.frame.slice(1), this.sizeLabel(variant.size)].join(' · ') : this.t('variant_unavailable');
      const stage = this.el('[data-art-stage]'); stage.dataset.frame = 'none';
      this.root.dataset.format = activeType || '';
      this.renderProductPreview(variant);
      this.measureConfigurationLayout();
      focusedOptions?.querySelector('[aria-pressed=true]')?.focus({ preventScroll: true });
    }
    renderProductPreview(variant) {
      const version = S.selected(this.state);
      if (this.root.dataset.screen !== 'configure' || !version) { this.productPreview.reset(); return; }
      this.productPreview.update({ artworkUrl: safeImage(version.previewUrl), versionId: version.id,
        format: variant?.type || this.state.configuration?.type || this.root.dataset.format,
        frame: variant?.frame || this.state.configuration?.frame || 'none', label: this.product.title });
    }
    async choose(choice) {
      choice = S.configurationChoice(this.state, this.variant(), choice);
      const variant = S.choose(this.variants(), choice);
      this.state.configuration = choice;
      if (!variant) { this.state.variantId = null; this.renderConfiguration(); await this.persist(); this.error(this.t('variant_unavailable')); return; }
      this.state.variantId = variant.id; this.state.cartAttempt = null;
      if (!variant.isDigital) this.state.physicalFrame = variant.frame;
      this.renderConfiguration(); this.error(''); await this.persist();
      this.event('configuration', { revision_id: this.state.selectedId, variant_id: variant.id });
    }
    openRetry() {
      if (!this.ready || this.busy || this.submitting || this.state.attempt) return;
      this.retrySource = structuredClone(S.selected(this.state));
      if (!this.retrySource) return;
      const count = S.photosFor(this.retrySource).length;
      this.el('[data-retry-same]').disabled = !count;
      this.el('[data-retry-same-label]').textContent = this.t(count === 1 ? 'retry_same_one' : 'retry_same_many');
      this.el('[data-retry-same-help]').textContent = this.t(count === 1 ? 'retry_same_help_one' : 'retry_same_help_many');
      const single = this.policy.selectionFileLimit === 1;
      this.el('[data-retry-new-label]').textContent = this.t(single ? 'retry_new_one' : 'retry_new_many');
      this.el('[data-retry-new-help]').textContent = this.t(single ? 'retry_new_help_one' : 'retry_new_help_many');
      this.el('[data-retry-retained]').textContent = (count ? '' : this.t('retry_sources_unavailable') + ' ') + this.t('retry_retained');
      this.el('[data-retry-error]').textContent = '';
      this.open(this.el('[data-retry-dialog]'));
    }
    async replacePhotos(files, source) {
      if (!source || this.busy || this.submitting || this.state.attempt || !files.length) return;
      this.busy = true;
      const dialog = this.el('[data-retry-dialog]'), same = this.el('[data-retry-same]'), upload = this.el('[data-retry-new]');
      same.disabled = true; upload.disabled = true; this.el('[data-retry-error]').textContent = '';
      this.render();
      try {
        const rejection = window.WallForgeUploadPolicy.fileError(this.policy, files);
        if (rejection) throw new Error(rejection);
        // Validate the entire selection before changing any accepted references.
        const captured = [];
        for (const file of files) {
          const owned = await window.WallForgePortraitHistory.capturePhoto(file);
          if (!this.photoPreparation) { const bitmap = await createImageBitmap(owned); bitmap.close(); }
          captured.push(owned);
        }
        if (!dialog.open || this.state.selectedId !== source.id || this.state.attempt) return;
        const before = this.state.photos, replacementFor = this.state.replacementFor;
        this.state.photos = captured.map(file => ({ id: uid(), file })); this.state.replacementFor = source.id;
        try { await this.persist(); }
        catch (error) { this.state.photos = before; this.state.replacementFor = replacementFor; throw error; }
        dialog.close(); this.error(''); await this.screen('upload');
        this.event('upload_accepted', { reference_count: files.length });
      } catch (error) {
        this.el('[data-retry-error]').textContent = this.t('photo_rejected') + (error.message && error.name === 'Error' ? ' ' + error.message : '');
      } finally {
        this.busy = false; same.disabled = !S.photosFor(source).length; upload.disabled = false; this.render();
      }
    }
    async preparePhoto(photo) {
      if (!this.photoPreparation) return null;
      const attemptId = photo.preparationAttemptId || photo.id;
      return window.WallForgePhotoPreparation.prepare(photo, this.photoPreparation, (state, handle) => {
        const current = this.state.photos.find(item => item.id === photo.id);
        if (!current || this.disposed || (current.preparationAttemptId || current.id) !== attemptId) return;
        const wasFailed = current.preparationState === 'failed';
        current.preparationState = state;
        if (handle) current.preparation = handle;
        this.persist().catch(() => {});
        // Background progress must not redraw thumbnails or disturb keyboard focus.
        if (!this.state.attempt && (wasFailed || state === 'failed')) this.renderPhotos();
      });
    }
    async addPhotos(files) {
      if (!this.policy || !this.creationAvailable || this.busy || this.state.attempt || !files.length) return;
      this.busy = true; this.error(''); this.render();
      let rejected = false, rejection = '';
      try {
        for (const selected of files) {
          const candidate = this.state.photos.map(photo => photo.file).concat(selected);
          const fileError = window.WallForgeUploadPolicy.fileError(this.policy, candidate);
          if (fileError) { rejected = true; rejection = fileError; continue; }
          let file;
          try {
            file = await window.WallForgePortraitHistory.capturePhoto(selected);
            if (!this.photoPreparation) { const decoded = await createImageBitmap(file); decoded.close(); }
          }
          catch (_) { rejected = true; continue; }
          const photo = { id: uid(), file, selectedAt: Date.now() };
          this.state.photos.push(photo);
          this.renderPhotos();
          if (this.photoPreparation) this.preparePhoto(photo).catch(() => {});
          this.event('upload_accepted', { reference_count: this.state.photos.length });
        }
        await this.persist();
        if (rejected) {
          const formats = this.policy.allowedMimeTypes.map(type => type.replace('image/', '').toUpperCase()).join(', ');
          const limit = Math.round(this.policy.maxFileSizeBytes / 1024 / 1024 * 10) / 10;
          this.error((rejection || this.t('photo_rejected')) + ' ' + formats + ' · ' + limit + ' MB');
        }
      } catch (_) { this.error(this.t('photo_rejected')); await this.persist().catch(() => this.error(this.t('load_failed'))); }
      finally { this.busy = false; this.render(); }
    }
    async request(url, payload, key, signal) {
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 30000);
      try {
        const response = payload === undefined ? await fetch(url, { cache: 'no-store', credentials: 'same-origin', signal: signal || controller.signal })
          : await window.wfFetch(url, { method: 'POST', signal: controller.signal, headers: key ? { 'Idempotency-Key': key } : {}, body: JSON.stringify(payload) });
        const data = await response.json();
        if (!response.ok) throw Object.assign(new Error('Request failed'), { code: data?.errorCode || data?.code || data?.status, status: response.status, httpStatus: response.status, data });
        return data;
      } finally { clearTimeout(timer); }
    }
    async generate(source = null) {
      if (this.busy || this.submitting || this.state.attempt) return;
      // Snapshot before the lock, image preparation or any network work.
      const snapshot = {
        designId: this.state.designId, sourceId: source?.id || this.state.replacementFor || null,
        photos: source ? S.photosFor(source) : structuredClone(this.state.photos),
        personalization: structuredClone(this.personalizationPayload),
        versions: this.state.versions.map(version => version.id).join(',')
      };
      if (!snapshot.photos.length) return;
      this.submitting = true;
      const create = async () => {
        const latest = await storage(this.key);
        if (latest?.attempt) { this.state = latest; this.render(); visible(this.el('[data-recover]'), true); return; }
        if (latest && latest.versions.map(version => version.id).join(',') !== snapshot.versions) { this.state = latest; this.render(); return; }
        if (latest) this.state = latest;
        await this.createGeneration(snapshot);
      };
      try { if (navigator.locks) await navigator.locks.request('wallforge-generation-' + this.key, create); else await create(); }
      catch (_) { this.error(this.t('load_failed')); }
      finally { this.submitting = false; this.render(); }
    }
    async createGeneration(snapshot) {
      if (!this.ready || !this.creationAvailable || this.busy || this.state.attempt || snapshot.designId !== this.state.designId) return;
      this.busy = true; this.state.photos = structuredClone(snapshot.photos);
      this.state.failedAttempt = null; this.state.generationIssue = null; this.showGeneration();
      try {
        if (window.WallForgeUploadPolicy.fileError(this.policy, snapshot.photos.map(photo => photo.file))) throw { code: 'PHOTO_REJECTED' };
        const handles = this.photoPreparation ? await Promise.all(snapshot.photos.map(photo => this.preparePhoto(photo))) : null;
        const prepared = handles ? { files: snapshot.photos.map(photo => photo.file), items: handles.map(handle => ({ outputWidth: handle.imageGeometry.width, outputHeight: handle.imageGeometry.height })) }
          : await window.WallForgeCustomerImagePreparation.prepareFiles(snapshot.photos.map(photo => photo.file));
        const identity = { productId: snapshot.designId, shop: window.Shopify?.shop || location.host, customerRef: window.WallForgeIdentity?.getCustomerRef(), uploadPolicyRevision: this.policy.revision };
        if (snapshot.personalization) identity.personalization = snapshot.personalization;
        window.WallForgeAudienceContext?.applyToPayload?.(identity);
        const batch = this.policy.uploader === 'multi';
        const payload = batch ? Object.assign(identity, { count: prepared.files.length, files: prepared.files.map(file => ({ filename: file.name, contentType: file.type, fileSize: file.size })) })
          : Object.assign(identity, { variantId: this.state.variantId, fileName: prepared.files[0].name, contentType: prepared.files[0].type, fileSize: prepared.files[0].size, imageGeometry: { width: prepared.items[0].outputWidth, height: prepared.items[0].outputHeight } });
        if (handles) handles.forEach((handle, index) => {
          const file = batch ? payload.files[index] : payload;
          file[batch ? 'filename' : 'fileName'] = handle.fileName || file[batch ? 'filename' : 'fileName'];
          Object.assign(file, { contentType: handle.contentType, fileSize: handle.fileSize,
            preparedPhoto: { id: handle.photoPreparationId, capabilityToken: handle.capabilityToken, occurrenceId: snapshot.photos[index].id } });
          snapshot.photos[index].preparation = handle;
        });
        this.state.attempt = { id: uid(), kind: 'generation', phase: 'init', payload, batch, files: prepared.files,
          photos: snapshot.photos, sourceId: snapshot.sourceId, designId: snapshot.designId, startedAt: Date.now(), capabilities: {} };
        await this.persist(); this.busy = false;
        await this.runGeneration();
      } catch (error) { this.failure(error); this.busy = false; this.render(); }
    }
    async runGeneration() {
      if (this.busy || !this.state.attempt) return;
      this.busy = true; visible(this.el('[data-recover]'), false); this.showGeneration();
      const attempt = this.state.attempt;
      try {
        if (attempt.phase === 'init') {
          this.el('[data-upload-status]').textContent = this.t('uploading');
          const endpoint = attempt.batch ? window.WallForgeAPI.endpoints.presignedBatch : window.WallForgeAPI.endpoints.init;
          const response = await window.wfInitFetch(endpoint, JSON.stringify(attempt.payload), { idempotencyKey: attempt.id });
          const data = await response.json();
          if (!response.ok) throw { status: response.status, code: data.errorCode || data.code || data.status };
          if (!data.jobId) throw { code: 'invalid_response' };
          attempt.init = data; attempt.jobId = data.jobId; attempt.capabilities = Object.assign(attempt.capabilities, data.capabilities);
          attempt.phase = data.status !== 'initialized' && !data.upload && !data.uploads ? 'poll' : 'transfer';
          await this.persist();
          this.renderGeneration();
        }
        if (attempt.phase === 'transfer') {
          const uploads = attempt.batch ? attempt.init.uploads : [attempt.init.upload];
          if (!uploads || uploads.length !== attempt.files.length) throw { code: 'invalid_response' };
          for (let i = 0; i < uploads.length; i++) {
            if (attempt.transferred?.includes(i)) continue;
            const upload = uploads[i];
            if (upload.prepared) continue;
            const result = await window.wfDirectUpload(upload.uploadUrl || upload.url, { file: attempt.files[i], headers: upload.headers || upload.uploadHeaders || { 'Content-Type': attempt.files[i].type } });
            if (!result.ok && !result.ambiguous) throw result;
            if (result.ok) { attempt.transferred = [...(attempt.transferred || []), i]; await this.persist(); }
          }
          attempt.phase = 'finish'; await this.persist();
        }
        if (attempt.phase === 'finish') {
          const initial = attempt.init;
          const payload = attempt.batch ? { uploadKey: initial.uploads[0].uploadKey, manifestItems: initial.uploads.map((upload, i) => ({ uploadKey: upload.uploadKey, position: i + 1 })), correlationId: initial.correlationId, ctx: initial.ctx }
            : { jobId: initial.jobId, assetKey: initial.upload.assetKey, uploadId: initial.upload.uploadId, capabilityToken: token(attempt.capabilities, 'uploadFinish') };
          const data = await this.request(window.WallForgeAPI.endpoints.finish, payload);
          attempt.capabilities = Object.assign(attempt.capabilities, data.capabilities); attempt.phase = 'poll'; await this.persist();
        }
        this.busy = false; this.el('[data-upload-status]').textContent = ''; this.poll();
      } catch (error) { this.busy = false; this.failure(error); this.render(); visible(this.el('[data-recover]'), Boolean(this.state.attempt)); }
    }
    failure(error, affectsAttempt = true) {
      const inGeneration = affectsAttempt && Boolean(this.state.attempt || this.state.flowScreen);
      const failedAttempt = this.state.attempt;
      const code = String(error?.code || error?.data?.failure?.code || '').toLowerCase();
      if (['capability_expired', 'capability_revoked', 'revision_unavailable'].includes(code)) {
        if (affectsAttempt) this.state.attempt = null;
        this.returnToUpload();
        return;
      }
      const terminal = ['failed', 'rejected', 'cancelled', 'expired'].includes(error?.backendStatus) || code === 'limit_reached';
      let key = 'request_failed';
      let changePhotos = false, allowance = false;
      if (code.includes('subject') || code.includes('detect') || code === 'photo_rejected') { key = 'detection_failed'; changePhotos = true; }
      if (['no_eligible_subjects', 'no_subject_detected'].includes(code)) key = 'detection_none';
      if (code.includes('uncertain') || code.includes('ambiguous')) key = this.state.photos.length < (this.policy?.selectionFileLimit || 1) ? 'detection_ambiguous_add' : 'detection_ambiguous_replace';
      if (['subject_mismatch', 'unsupported_subject_type'].includes(code)) key = 'detection_unsupported';
      if (['too_many_subjects', 'subject_candidate_limit_exceeded', 'insufficient_design_slots'].includes(code)) { key = 'subject_limit'; changePhotos = true; }
      if (code === 'subject_analysis_failed') { key = 'detection_service_failed'; changePhotos = false; }
      if (code === 'photo_preparation_failed') { key = 'photo_upload_failed'; changePhotos = true; }
      if (code === 'portrait_subject_count_mismatch') {
        const maximum = this.policy?.subjectCapacity?.max;
        key = maximum === 1 ? 'capacity_mismatch_one' : maximum === 2 ? 'capacity_mismatch_two' : 'capacity_mismatch_many'; changePhotos = true;
      }
      if (code === 'portrait_subject_count_unavailable') { key = 'capacity_unavailable'; changePhotos = true; }
      if (code === 'portrait_pet_not_found') { key = 'capacity_pet_missing'; changePhotos = true; }
      if (!changePhotos && (code.includes('limit') || code.includes('quota') || code.includes('allowance'))) { key = 'allowance'; allowance = true; }
      if (code.includes('expir') || code.includes('revoked')) key = 'access_expired';
      if (code === 'poll_paused' || code === 'e2003') key = 'delayed_reconnect';
      if (code === 'upload_policy_stale') { key = 'policy_changed'; this.discover(true).then(() => this.render()).catch(() => {}); }
      if (affectsAttempt && (terminal || allowance || (error?.status >= 400 && error?.status < 500 && error.status !== 408 && error.status !== 429))) {
        this.state.attempt = null; this.persist().catch(() => {});
      }
      if (inGeneration) {
        clearTimeout(this.generationDelay);
        this.state.generationIssue = { key, changePhotos, allowance };
        this.state.flowScreen = this.state.attempt ? 'generation-delayed' : 'generation-error';
        if (!this.state.attempt && failedAttempt?.kind === 'edit') this.state.failedAttempt = { kind: 'edit', sourceId: failedAttempt.sourceId, prompt: failedAttempt.prompt };
        this.render(); this.persist().catch(() => {});
      }
      this.error(this.t(key));
    }
    async poll() {
      if (this.polling || !this.state.attempt) return;
      this.polling = true; this.busy = true; visible(this.el('[data-recover]'), false); this.showGeneration();
      const attempt = this.state.attempt;
      try {
        const editing = attempt.kind === 'edit', scope = editing ? 'editStatus' : 'uploadStatus';
        const url = new URL((editing ? window.WallForgeAPI.endpoints.editStatus : window.WallForgeAPI.endpoints.status) + '/' + encodeURIComponent(editing ? attempt.editId : attempt.jobId));
        url.searchParams.set('capabilityToken', token(attempt.capabilities, scope));
        const monitor = window.WallForgeStatusCoordinator.create({
          expiresAt: attempt.capabilities[scope]?.expiresAt,
          isCurrent: () => this.state.attempt?.id === attempt.id && this.root.isConnected,
          read: async signal => {
            const data = await this.request(url.href, undefined, undefined, signal);
            const status = String(data.status).toLowerCase();
            if (['complete', 'succeeded'].includes(status)) data.status = 'completed';
            if (['rejected', 'cancelled', 'expired'].includes(status)) { data.status = 'failed'; data.failure = data.failure || { code: status }; }
            return data;
          },
          onPending: data => { attempt.status = data.status; attempt.processingStage = data.processingStage; this.renderGeneration(); },
          onUnreachable: () => { if (this.state.flowScreen) { this.state.flowScreen = 'generation-delayed'; this.render(); } }
        });
        const data = await monitor.promise;
        if (data.canceled || this.state.attempt?.id !== attempt.id) return;
        if (data.status === 'failed') throw { code: data.failure?.code || 'failed', backendStatus: 'failed' };
        if (editing) {
          attempt.phase = 'edit_accept'; await this.persist();
          this.renderGeneration();
          const accepted = await this.request(window.WallForgeAPI.endpoints.editAccept + '/' + encodeURIComponent(attempt.editId) + '/a', { capabilityToken: token(attempt.capabilities, 'editAccept') });
          const selectedUrl = new URL(window.WallForgeAPI.endpoints.status + '/' + encodeURIComponent(attempt.jobId));
          selectedUrl.searchParams.set('capabilityToken', token(attempt.sourceCapabilities, 'uploadStatus')); selectedUrl.searchParams.set('revisionId', accepted.artwork.revisionId);
          await this.completed(attempt, await this.request(selectedUrl.href), attempt.sourceCapabilities);
        } else await this.completed(attempt, data, attempt.capabilities);
      } catch (error) { this.failure(error); visible(this.el('[data-recover]'), Boolean(this.state.attempt)); }
      finally { this.polling = false; this.busy = false; this.render(); }
    }
    async completed(attempt, data, capabilities) {
      if (this.state.attempt?.id !== attempt.id) return;
      const wasCreating = Boolean(this.state.flowScreen);
      const version = this.versionFrom(data, capabilities);
      attempt.processingStage = 'preview_ready'; this.renderGeneration();
      const image = new Image(); image.src = version.previewUrl;
      let timer;
      try { await Promise.race([image.decode(), new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Preview unavailable')), 20000); })]); }
      finally { clearTimeout(timer); }
      if (this.state.attempt?.id !== attempt.id) return;
      if (!S.complete(this.state, attempt.id, version)) throw { code: 'ARTWORK_BINDING_MISSING' };
      clearTimeout(this.generationDelay); this.state.flowScreen = null; this.state.failedAttempt = null; this.state.generationIssue = null;
      this.cartRestoreUnavailable = false;
      await this.persist(); this.event('generation_completed', { job_id: version.jobId, revision_id: version.id });
      history.replaceState({ ...history.state, wallforgeDesign: this.state.designId, screen: 'result' }, '', location.href);
      this.error(''); visible(this.el('[data-recover]'), false);
      this.render();
      if (wasCreating && !document.querySelector('dialog[open]')) { this.root.scrollIntoView({ block: 'start' }); this.root.tabIndex = -1; this.root.focus({ preventScroll: true }); }
    }
    versionFrom(data, capabilities) {
      return { designId: this.state.designId, id: data.artwork?.revisionId, binding: data.artwork?.binding, jobId: data.jobId,
        previewUrl: safeImage(data.previewUrl), cartPreviewUrl: safeImage(data.cartPreviewUrl), printRef: data.printRef,
        capabilities: Object.assign({}, capabilities, data.capabilities), personalization: data.personalization, aspectRatio: data.artworkProfile?.aspectRatio,
        previewExpiresAt: data.tokens?.preview?.expiresAt || null, previewRefreshedAt: Date.now(),
        explicitVariantRestrictions: data.artworkProfile?.explicitVariantRestrictions,
        allowedVariantIds: data.artworkProfile?.allowedVariantIds, completedAt: Date.now() };
    }
    async refreshSelected() {
      const selected = S.selected(this.state); if (!selected) return;
      await this.refreshVersion(selected);
    }
    async refreshHistory(force = false) {
      if (!this.ready || this.disposed || this.historyRefresh || this.state.attempt) return;
      this.historyRefresh = true;
      try {
        for (const version of S.completedVersions(this.state)) {
          if (this.disposed) break;
          const expires = Date.parse(version.previewExpiresAt || '');
          if (force || !version.previewRefreshedAt || Date.now() - version.previewRefreshedAt >= 45 * 60000
            || (Number.isFinite(expires) && expires <= Date.now() + 60000)) {
            try { await this.refreshVersion(version, true); }
            catch (_) { /* One unavailable local record must not stop the other previews. */ }
          }
        }
      } finally { this.historyRefresh = false; }
    }
    async refreshVersion(version, silent = false) {
      if (this.disposed || version.unavailable) return;
      if (this.previewRefreshes.has(version.id)) return this.previewRefreshes.get(version.id);
      const refresh = async () => {
        try {
          const url = new URL(window.WallForgeAPI.endpoints.status + '/' + encodeURIComponent(version.jobId));
          url.searchParams.set('revisionId', version.id); url.searchParams.set('capabilityToken', token(version.capabilities, 'uploadStatus'));
          const data = await this.request(url.href);
          if (data.jobId !== version.jobId || data.artwork?.revisionId !== version.id || !data.artwork?.binding || !safeImage(data.previewUrl)) throw { code: 'ARTWORK_BINDING_MISSING' };
          if (this.disposed || !this.state.versions.includes(version)) return;
          const completedAt = version.completedAt;
          Object.assign(version, this.versionFrom(data, version.capabilities), { completedAt });
          await this.persist(); this.render();
          const zoom = this.el('[data-lightbox][open] img');
          if (zoom && this.state.selectedId === version.id && zoom.src !== version.previewUrl) zoom.src = version.previewUrl;
        } catch (error) {
          if (this.disposed || !this.state.versions.includes(version)) return;
          if (['capability_expired', 'capability_revoked', 'revision_unavailable', 'not_found'].includes(String(error?.code).toLowerCase()) || error?.status === 404 || error?.status === 410) {
            version.unavailable = true;
            if (this.state.selectedId === version.id && !this.state.attempt) this.returnToUpload(version);
            else { await this.persist(); this.render(); }
          } else if (!silent && this.state.selectedId === version.id) this.failure(error, false);
        }
      };
      const pending = refresh(); this.previewRefreshes.set(version.id, pending);
      try { await pending; } finally { this.previewRefreshes.delete(version.id); }
    }
    returnToUpload(version = null) {
      if (version) version.unavailable = true;
      this.state.selectedId = null; this.state.screen = 'upload'; this.state.flowScreen = null;
      this.state.failedAttempt = null; this.state.generationIssue = null;
      this.cartRestoreUnavailable = false;
      clearTimeout(this.generationDelay);
      this.root.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
      const url = new URL(location.href); url.searchParams.delete('wf_revision'); url.searchParams.delete('wf_saved');
      if (url.hash.startsWith('#wf-portrait=')) url.hash = '';
      history.replaceState({ ...history.state, wallforgeDesign: this.state.designId, screen: 'upload' }, '', url.href);
      this.error(''); visible(this.el('[data-recover]'), false);
      this.render(); this.persist().catch(() => {});
    }
    async edit(prompt) {
      if (this.busy || this.submitting || this.state.attempt || !this.editSource || !prompt.trim()) return;
      this.submitting = true;
      const source = structuredClone(this.editSource);
      const create = async () => {
        const latest = await storage(this.key);
        if (latest?.attempt) { this.state = latest; this.el('[data-edit-dialog]').close(); this.render(); visible(this.el('[data-recover]'), true); return; }
        if (latest) this.state = latest;
        this.state.failedAttempt = null; this.state.generationIssue = null;
        this.state.attempt = { id: uid(), kind: 'edit', phase: 'edit_init', jobId: source.jobId, sourceId: source.id, photos: S.photosFor(source), sourceAssets: structuredClone(source.sourceAssets || []), designId: this.state.designId, prompt: prompt.trim(), sourceCapabilities: source.capabilities, capabilities: {}, startedAt: Date.now() };
        await this.persist(); this.el('[data-edit-dialog]').close(); await this.runEdit();
      };
      try { if (navigator.locks) await navigator.locks.request('wallforge-generation-' + this.key, create); else await create(); }
      catch (_) { this.error(this.t('load_failed')); }
      finally { this.submitting = false; this.render(); }
    }
    async runEdit() {
      if (this.busy || !this.state.attempt) return;
      this.busy = true; this.showGeneration();
      const attempt = this.state.attempt;
      try {
        const data = await this.request(window.WallForgeAPI.endpoints.editCreate, { jobId: attempt.jobId, sourceRevisionId: attempt.sourceId, prompt: attempt.prompt, idempotencyKey: attempt.id, capabilityToken: token(attempt.sourceCapabilities, 'editCreate') });
        attempt.editId = data.editId; attempt.capabilities = data.capabilities; attempt.phase = 'edit_poll'; await this.persist(); this.busy = false; this.poll();
      } catch (error) { this.busy = false; this.failure(error); this.render(); visible(this.el('[data-recover]'), Boolean(this.state.attempt)); }
    }
    async recover() {
      if (this.busy) return;
      if (!this.ready) { await this.init(); return; }
      const attempt = this.state.attempt;
      if (attempt?.kind === 'edit') {
        if (attempt.phase === 'edit_init') await this.runEdit(); else await this.poll();
      } else if (attempt?.phase === 'poll') await this.poll();
      else if (attempt) await this.runGeneration();
      else {
        this.error(''); visible(this.el('[data-recover]'), false);
        if (S.selected(this.state)) await this.refreshSelected();
        else await this.refreshHistory(true);
      }
    }
    async savePortrait(form) {
      const status = this.el('[data-save-status]');
      const email = form.elements.email; email.value = email.value.trim();
      if (!email.checkValidity()) {
        this.saveValidationError = true; email.setAttribute('aria-invalid', 'true'); status.setAttribute('role', 'alert');
        status.textContent = this.t(email.value ? 'email_invalid' : 'email_required'); email.focus(); return;
      }
      this.saveValidationError = false; email.removeAttribute('aria-invalid'); status.setAttribute('role', 'status');
      const viewId = this.saveViewId, address = email.value, currentView = () => this.saveViewId === viewId;
      if (!this.discovery?.savedWork?.saveUrl) { status.textContent = this.t('save_unavailable'); return; }
      if (this.expiredSavedId) {
        const button = form.querySelector('[type=submit]'); if (button.disabled) return; button.disabled = true; email.disabled = true;
        try {
          await this.request(this.discovery.savedWork.saveUrl + '/' + encodeURIComponent(this.expiredSavedId) + '/resend', { email: address });
          if (currentView()) status.textContent = this.t('renew_requested');
        } catch (_) { if (currentView()) status.textContent = this.t('save_failed'); }
        finally { if (currentView()) { button.disabled = false; email.disabled = false; } }
        return;
      }
      const button = form.querySelector('[type=submit]'); if (button.disabled) return; button.disabled = true; email.disabled = true;
      const source = structuredClone(this.saveSource), variantId = this.saveVariantId;
      try {
        const data = await this.request(this.discovery.savedWork.saveUrl, { email: address, jobId: source.jobId, revisionId: source.id, artworkBinding: source.binding, variantId, capabilityToken: token(source.capabilities, 'uploadStatus') });
        if (data.saved !== true) throw new Error('Save was not established');
        this.state.savedPortraits = (this.state.savedPortraits || []).filter(item => item.id !== data.id);
        this.state.savedPortraits.unshift({ id: data.id, revisionId: source.id, variantId, savedAt: Date.now() });
        await this.persist().catch(() => {});
        if (!currentView()) return;
        visible(this.el('[data-save-actions]'), true);
        this.error('');
        status.textContent = data.emailAccepted ? this.t('save_accepted') : this.t('saved_email_failed');
        if (!data.emailAccepted) button.textContent = this.t('resend');
      } catch (_) { if (currentView()) status.textContent = this.t('save_failed'); }
      finally { if (currentView()) { button.disabled = false; email.disabled = false; } }
    }
    async resume() {
      if (!this.discovery?.savedWork?.saveUrl || !this.resumeIdentity) { this.error(this.t('save_unavailable')); return; }
      const split = this.resumeIdentity.indexOf('.');
      const savedId = this.resumeIdentity.slice(0, split), access = this.resumeIdentity.slice(split + 1);
      if (split < 1 || !/^[a-zA-Z0-9]{64}$/.test(access)) { this.returnToUpload(); return; }
      try {
        const url = new URL(this.discovery.savedWork.saveUrl + '/' + encodeURIComponent(savedId)); url.searchParams.set('token', access);
        const data = await this.request(url.href);
        if (String(data.productId).split('/').pop() !== this.state.designId) throw new Error('Wrong design');
        const portrait = data.portrait;
        const version = this.versionFrom(portrait, data.capabilities);
        if (!version.previewUrl || !version.binding) throw new Error('Missing artwork');
        const existing = this.state.versions.findIndex(item => item.id === version.id);
        if (existing >= 0) Object.assign(this.state.versions[existing], version); else this.state.versions.push(version);
        this.state.selectedId = version.id; this.state.screen = 'result';
        S.restoreSources(this.state);
        history.replaceState({ ...history.state, wallforgeDesign: this.state.designId, screen: 'result' }, '', location.href);
        const choice = this.variants().find(item => item.id === String(data.configuration.variantId) && item.available);
        this.state.variantId = choice?.id || null;
        this.state.configuration = data.configuration.variantId ? { id: String(data.configuration.variantId), type: choice?.type, frame: choice?.frame, size: choice?.size } : null;
        this.state.savedPortraits = (this.state.savedPortraits || []).filter(item => item.id !== savedId);
        this.state.savedPortraits.unshift({ id: savedId, revisionId: version.id, variantId: choice?.id || null, savedAt: Date.now() });
        await this.persist();
        this.error('');
        if (data.configuration.variantId && !choice) this.error(this.t('variant_unavailable'));
      } catch (error) {
        if (error.status === 410 || error.status === 404 || error.data?.status === 'revision_unavailable') { this.returnToUpload(); return; }
        this.error(this.t('request_failed'));
      }
    }
    async addBag() {
      if (this.busy || !this.variant() || !S.selected(this.state)) return;
      const selected = structuredClone(S.selected(this.state)), variant = structuredClone(this.variant());
      this.busy = true; this.renderConfiguration(); this.error('');
      const add = async () => {
        const cartUrl = (window.Shopify?.routes?.root || '/') + 'cart';
        const latest = await storage(this.key);
        this.state.cartOperations = latest?.cartOperations || this.state.cartOperations || [];
        let operation = this.state.cartOperations.find(item => item.revisionId === selected.id && item.variantId === variant.id)
          || latest?.cartAttempt || this.state.cartAttempt;
        if (operation && (operation.revisionId !== selected.id || operation.variantId !== variant.id)) operation = null;
        this.state.cartAttempt = operation;
        if (operation) {
          const cart = await this.request(cartUrl + '.js');
          const existingItem = S.findCartItem(cart, operation.id);
          if (existingItem) {
            operation.done = true;
            await this.reportBag(operation, selected, variant, existingItem.properties);
            this.openBag(variant); return;
          }
          if (operation.done) {
            this.state.cartOperations = this.state.cartOperations.filter(item => item.id !== operation.id); operation = null;
          } else if (operation.uncertain) { this.error(this.t('bag_uncertain')); return; }
        }
        const freshProduct = await this.request((window.Shopify?.routes?.root || '/') + 'products/' + encodeURIComponent(this.product.handle) + '.js');
        const freshVariant = freshProduct.variants.find(item => String(item.id) === variant.id);
        this.product = freshProduct; this.catalog = S.catalog(freshProduct, window.WallforgeVariantSemantics);
        if (!freshVariant?.available || freshVariant.price !== variant.price) { this.error(this.t('price_changed')); this.renderConfiguration(); return; }
        operation = operation || { id: uid(), revisionId: selected.id, variantId: variant.id, uncertain: false };
        this.state.cartAttempt = operation;
        if (!this.state.cartOperations.some(item => item.id === operation.id)) this.state.cartOperations.push(operation);
        await this.persist();
        let properties = S.cartProperties(selected, operation.id, variant);
        properties = window.WallForgeAudienceContext?.decorateCartProperties?.(properties) || properties;
        operation.uncertain = true; await this.persist();
        const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 30000);
        let response;
        try { response = await fetch(cartUrl + '/add.js', { method: 'POST', credentials: 'same-origin', signal: controller.signal, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: variant.id, quantity: 1, properties }] }) }); }
        finally { clearTimeout(timer); }
        if (!response.ok) {
          if (response.status >= 400 && response.status < 500) { operation.uncertain = false; await this.persist(); }
          throw new Error('Cart rejected');
        }
        operation.done = true; await this.persist();
        await this.reportBag(operation, selected, variant, properties);
        this.openBag(variant);
      };
      try { if (navigator.locks) await navigator.locks.request('wallforge-bag-' + this.key, add); else await add(); }
      catch (_) { this.error(this.t(this.state.cartAttempt?.uncertain ? 'bag_uncertain' : 'bag_failed')); }
      finally { this.busy = false; this.renderConfiguration(); }
    }
    openBag(variant) {
      const cart = document.getElementById('CartDrawer')?.wallforgeCartDrawer;
      if (!cart) { location.assign((window.Shopify?.routes?.root || '/') + 'cart'); return; }
      const trigger = this.el('[data-add-bag]');
      if (trigger) { trigger.setAttribute('aria-expanded', 'true'); cart.drawer.activeSource = trigger; }
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { productVariantId: variant.id } }));
    }
    async reportBag(operation, selected, variant, properties) {
      if (operation.reported) return;
      operation.reported = true; await this.persist();
      window.trackWallForgeCartAdd?.(window.WallForgeAPI.endpoints.cartAdd, { jobId: selected.jobId, shop: window.Shopify?.shop || location.host, shopifyVariantId: variant.id, quantity: 1, productId: String(this.product.id), customerRef: window.WallForgeIdentity?.getCustomerRef(), artworkBinding: selected.binding, properties, metadata: { artworkRevisionId: selected.id, cartOperationId: operation.id } }, token(selected.capabilities, 'uploadStatus'));
      this.event('bag_added', { revision_id: selected.id, variant_id: variant.id, operation_id: operation.id });
    }
  }
  function mountDiscovery(root) {
    if (root.dataset.mounted) return; root.dataset.mounted = 'true';
    const open = trigger => {
      const dialog = root.querySelector('[data-help-library]');
      document.querySelectorAll('dialog[open]').forEach(node => node.close());
      dialog.returnFocus = trigger || document.querySelector('[data-menu-open]'); dialog.showModal();
    };
    document.addEventListener('click', event => {
      const trigger = event.target.closest('[data-wf-open-help]');
      if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault(); open(trigger.closest('dialog') ? document.querySelector('[data-menu-open]') : trigger);
    });
    root.querySelectorAll('[data-discovery-close]').forEach(button => button.onclick = () => button.closest('dialog').close());
    root.querySelectorAll('dialog').forEach(dialog => {
      containDialogFocus(dialog);
      dialog.addEventListener('close', () => {
        if (!document.querySelector('dialog[open]')) dialog.returnFocus?.focus();
        const url = new URL(location.href); if (url.searchParams.get('wf_view') === 'help') { url.searchParams.delete('wf_view'); history.replaceState(history.state, '', url.href); }
      });
      dialog.addEventListener('click', event => { const r = dialog.getBoundingClientRect(); if (event.target === dialog && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom)) dialog.close(); });
    });
    const view = new URL(location.href).searchParams.get('wf_view');
    if (view === 'help') open();
  }
  function mountSwipeRail(rail) {
    if (rail.dataset.swipeMounted) return;
    rail.dataset.swipeMounted = 'true';
    let gesture, suppressClick = false;
    const measure = () => rail.classList.toggle('is-scrollable', rail.scrollWidth > rail.clientWidth + 1);
    new ResizeObserver(measure).observe(rail);
    new MutationObserver(measure).observe(rail, { childList: true });
    rail.addEventListener('dragstart', event => event.preventDefault());
    rail.addEventListener('pointerdown', event => {
      suppressClick = false;
      if (event.pointerType === 'touch' || event.button !== 0 || rail.scrollWidth <= rail.clientWidth + 1) return;
      gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, left: rail.scrollLeft, dragging: false };
    });
    rail.addEventListener('pointermove', event => {
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
      if (!gesture.dragging) {
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 6) { gesture = null; return; }
        if (Math.abs(dx) < 6) return;
        gesture.dragging = true; suppressClick = true;
        rail.setPointerCapture(event.pointerId); rail.classList.add('is-dragging');
      }
      event.preventDefault(); rail.scrollLeft = gesture.left - dx;
    });
    const finish = event => {
      if (!gesture || event.pointerId !== gesture.id) return;
      gesture = null; rail.classList.remove('is-dragging');
      if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
    };
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(name => rail.addEventListener(name, finish));
    rail.addEventListener('pointerleave', event => { if (!gesture?.dragging) finish(event); });
    rail.addEventListener('click', event => {
      if (!suppressClick || event.detail === 0) return;
      event.preventDefault(); event.stopImmediatePropagation(); suppressClick = false;
    }, true);
    measure();
  }
  function mount() {
    const cartRoot = document.querySelector('#CartDrawer');
    if (document.querySelector('[data-wf-navigation]') && cartRoot && !cartRoot.wallforgeCartDrawer && window.theme?.CartDrawer) {
      const cart = new window.theme.CartDrawer();
      cartRoot.wallforgeCartDrawer = cart;
      const updateCount = cart.cartForm.updateCount.bind(cart.cartForm);
      cart.cartForm.updateCount = count => {
        updateCount(count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: { item_count: count } } }));
      };
    }
    document.querySelectorAll('[data-wf-studio]').forEach(root => { if (!root.studio && window.WallForgeProductPreview) root.studio = new Studio(root); });
    document.querySelectorAll('[data-wf-navigation]').forEach(root => {
      if (root.dataset.mounted) return; root.dataset.mounted = 'true';
      const dialog = root.querySelector('dialog'), trigger = root.querySelector('[data-menu-open]');
      document.addEventListener('cart:updated', event => {
        const count = event.detail?.cart?.item_count;
        if (!Number.isSafeInteger(count) || count < 0) return;
        const badge = root.querySelector('[data-wf-cart-count]'); badge.textContent = String(count); visible(badge, count > 0);
      });
      containDialogFocus(dialog);
      trigger.onclick = () => { dialog.showModal(); trigger.setAttribute('aria-expanded', 'true'); };
      root.querySelector('[data-menu-close]').onclick = () => dialog.close();
      dialog.addEventListener('close', () => { trigger.setAttribute('aria-expanded', 'false'); if (!document.querySelector('dialog[open]')) trigger.focus(); });
      dialog.addEventListener('click', event => { if (event.target === dialog && event.clientX > dialog.getBoundingClientRect().right) dialog.close(); });
    });
    document.querySelectorAll('[data-wf-discovery]').forEach(mountDiscovery);
    document.querySelectorAll('.wf-studio__rail,.wf-studio__version-thumbs,.wf-studio__previous-list,.wf-studio__scale').forEach(mountSwipeRail);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount); else mount();
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', event => {
    const roots = [...event.target.querySelectorAll('[data-wf-studio]')];
    if (event.target.matches('[data-wf-studio]')) roots.push(event.target);
    roots.forEach(root => { root.studio?.destroy(); delete root.studio; });
  });
})();
